import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { toast } from 'react-toastify';
import { DEFAULT_AML_SETTINGS } from '../../utils/amlSchema';
import { fetchAmlSettings, saveAmlSettingsApi } from '../../utils/amlApi';
import { subscribeAmlSettings } from '../../utils/amlFirestoreService';

const fieldSx = {
  '& .MuiInputBase-input': { fontSize: 14, py: 0.9, fontWeight: 600 },
  '& .MuiInputLabel-root': { fontSize: 13, fontWeight: 600 },
  '& .MuiInputBase-root': { minHeight: 40 }
};

const switchSx = {
  ml: 0,
  mr: 1.1,
  my: 0,
  '& .MuiFormControlLabel-label': { fontSize: 13.5, fontWeight: 650, color: '#334155' }
};

function InvoiceAmlSettingsSection({ canEdit }) {
  const [form, setForm] = useState({ ...DEFAULT_AML_SETTINGS });
  const [saving, setSaving] = useState(false);
  const readOnly = !canEdit;

  useEffect(() => {
    const unsub = subscribeAmlSettings((data) => {
      setForm({ ...DEFAULT_AML_SETTINGS, ...(data || {}) });
    });
    fetchAmlSettings()
      .then((r) => {
        if (r?.settings) setForm({ ...DEFAULT_AML_SETTINGS, ...r.settings });
      })
      .catch(() => {});
    return () => unsub?.();
  }, []);

  const setField = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveAmlSettingsApi(form);
      toast.success('MASAK ayarları kaydedildi');
    } catch (err) {
      toast.error(err?.message || 'Ayar kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        border: '1px solid #e2e8f0',
        p: 1.5,
        bgcolor: '#fff',
        height: 'fit-content',
        alignSelf: 'stretch',
        minWidth: 0,
        maxWidth: '100%',
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}
    >
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 800,
          color: '#475569',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          mb: 1.1
        }}
      >
        MASAK / AML
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.25, mb: 1.25 }}>
        <FormControlLabel
          control={
            <Switch checked={Boolean(form.enabled)} onChange={setField('enabled')} disabled={readOnly} />
          }
          label={form.enabled ? 'Aktif' : 'Kapalı'}
          sx={switchSx}
        />
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(form.screenBankMovements)}
              onChange={setField('screenBankMovements')}
              disabled={readOnly}
            />
          }
          label="Banka"
          sx={switchSx}
        />
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(form.screenInvoiceMatch)}
              onChange={setField('screenInvoiceMatch')}
              disabled={readOnly}
            />
          }
          label="Eşleşme"
          sx={switchSx}
        />
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(form.screenBeforeEdm)}
              onChange={setField('screenBeforeEdm')}
              disabled={readOnly}
            />
          }
          label="Kolaysoft"
          sx={switchSx}
        />
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(form.listSyncEnabled)}
              onChange={setField('listSyncEnabled')}
              disabled={readOnly}
            />
          }
          label="Liste"
          sx={switchSx}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(3, minmax(0, 1fr)) auto'
          },
          gap: 1.1,
          alignItems: 'center',
          minWidth: 0
        }}
      >
        <TextField
          select
          label="Mod"
          size="small"
          fullWidth
          value={form.mode || 'monitor'}
          onChange={setField('mode')}
          disabled={readOnly}
          sx={fieldSx}
        >
          <MenuItem value="monitor">İzleme</MenuItem>
          <MenuItem value="enforcement">Uygulama</MenuItem>
        </TextField>
        <TextField
          select
          label="Olası eşleşme"
          size="small"
          fullWidth
          value={form.possibleMatchSendBehavior || 'allow'}
          onChange={setField('possibleMatchSendBehavior')}
          disabled={readOnly}
          sx={fieldSx}
        >
          <MenuItem value="allow">İzin ver</MenuItem>
          <MenuItem value="hold">Beklet</MenuItem>
        </TextField>
        <TextField
          select
          label="Güçlü eşleşme"
          size="small"
          fullWidth
          value={form.strongMatchSendBehavior || 'block'}
          onChange={setField('strongMatchSendBehavior')}
          disabled={readOnly}
          sx={fieldSx}
        >
          <MenuItem value="block">Durdur</MenuItem>
          <MenuItem value="allow">İzin ver</MenuItem>
        </TextField>
        {!readOnly ? (
          <Button
            variant="contained"
            size="small"
            disabled={saving}
            onClick={handleSave}
            sx={{ textTransform: 'none', fontWeight: 800, fontSize: 13.5, minHeight: 40, px: 2 }}
          >
            Kaydet
          </Button>
        ) : null}
      </Box>
    </Paper>
  );
}

export default InvoiceAmlSettingsSection;
